/**
 * Nordic Circle storefront behaviour.
 *
 * Only what the design needs on top of Horizon: routing a size pill's variant
 * into its form. Everything else — the AJAX add, cart section refresh and
 * drawer opening — is Horizon's product-form-component.
 */
(() => {
  /**
   * Horizon's product-form-component builds its payload with
   * `new FormData(form)`, which excludes the button that submitted the form.
   * So the clicked pill copies its variant into the form's hidden input.
   *
   * This must run on `click`, not on `submit`. Both this and Horizon's handler
   * would be document-level capture listeners, and Horizon's module registers
   * first — so a submit-time write lands after it has already read the input,
   * adding whichever size was clicked previously. A click handler always
   * completes before submit is dispatched, since submit is the click's
   * default action.
   */
  document.addEventListener(
    'click',
    (event) => {
      const pill = event.target.closest?.('[data-nc-variant]');
      if (!pill || pill.disabled) return;

      const input = pill.form?.querySelector('[ref="variantId"]');
      if (input) input.value = pill.dataset.ncVariant;
    },
    true
  );

  /**
   * The card's image and title open the quick view rather than navigating, so a
   * shopper browsing the collection stays on the page. They are still real
   * links, so they keep their href for new-tab clicks, crawlers and no-JS.
   *
   * They sit outside <quick-add-component>, so Horizon's declarative
   * `on:click="quick-add-component/handleClick"` binding cannot reach them;
   * the click is forwarded to the card's component instead. handleClick calls
   * preventDefault itself, which is what suppresses the navigation.
   */
  document.addEventListener('click', (event) => {
    // Let modified clicks (new tab, download, etc.) behave natively.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== undefined && event.button !== 0) return;

    const trigger = event.target.closest?.('[data-nc-quickview]');
    if (!trigger) return;

    const component = trigger.closest('.nc-card')?.querySelector('quick-add-component');
    if (typeof component?.handleClick === 'function') component.handleClick(event);
  });

  /**
   * Product-page close button: step back when the visitor came from this shop,
   * so they land where they were in the collection with scroll intact. Otherwise
   * the anchor's own href handles it (a direct visit has nothing to go back to).
   */
  document.addEventListener('click', (event) => {
    const close = event.target.closest?.('[data-nc-product-close]');
    if (!close || event.defaultPrevented) return;

    const cameFromShop = document.referrer && new URL(document.referrer).origin === location.origin;
    if (cameFromShop && history.length > 1) {
      event.preventDefault();
      history.back();
    }
  });

  /**
   * Cart page: the checkout button stays disabled until the binding pre-order
   * terms are accepted. The checkbox also posts as a cart attribute, so the
   * acceptance is recorded on the order rather than only in the browser.
   */
  const syncConsent = () => {
    const consent = document.querySelector('[data-nc-consent]');
    const submit = document.querySelector('[data-nc-checkout]');
    if (consent && submit) submit.disabled = !consent.checked;
  };

  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-nc-consent]')) syncConsent();
  });

  // The cart section re-renders after quantity edits, so re-sync afterwards.
  document.addEventListener('cart:update', syncConsent);
  document.addEventListener('DOMContentLoaded', syncConsent);
  syncConsent();
})();
